import * as Clipboard from "expo-clipboard";
import { Check, Copy } from "lucide-react-native";
import { FC, useCallback, useState } from "react";

import { useLocalization } from "@/hooks";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Button,
  ButtonText,
  ErrorWithRetry,
  Heading,
  HStack,
  Pressable,
  Skeleton,
  Text,
  VStack,
} from "../ui";

interface ShareResultsModalProps {
  error: string | null;
  isOpen: boolean;
  link: string | null;
  loading: boolean;
  onClose: () => void;
  onRetry: () => void;
}

const ShareResultsModal: FC<ShareResultsModalProps> = ({
  error,
  isOpen,
  link,
  loading,
  onClose,
  onRetry,
}) => {
  const { i18n } = useLocalization();
  const [copied, setCopied] = useState(false);

  const handleCopyLink = useCallback(async () => {
    if (!link) return;
    await Clipboard.setStringAsync(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [link]);

  return (
    <AlertDialog isOpen={isOpen} onClose={onClose}>
      <AlertDialogBackdrop />
      <AlertDialogContent className="gap-6 max-w-lg">
        <AlertDialogHeader>
          <Heading size="lg">{i18n.t("studies.shareTitle")}</Heading>
        </AlertDialogHeader>
        <AlertDialogBody>
          <VStack className="gap-8">
            <Text>{i18n.t("studies.shareDescription")}</Text>
            {loading ? (
              <Skeleton className="h-10 w-full rounded-xl" />
            ) : error ? (
              <ErrorWithRetry error={error} onRetry={onRetry} />
            ) : (
              link && (
                <HStack className="mt-2 items-center bg-background-50 border border-outline-200 rounded-xl pl-4 pr-2 py-2 gap-2">
                  <Text
                    className="flex-1 text-sm text-typography-700 py-1"
                    numberOfLines={1}
                  >
                    {link}
                  </Text>
                  <Pressable className="p-1.5" onPress={handleCopyLink}>
                    {copied ? (
                      <Check color="rgb(var(--color-success-600))" size={16} />
                    ) : (
                      <Copy
                        color="rgb(var(--color-typography-600))"
                        size={16}
                      />
                    )}
                  </Pressable>
                </HStack>
              )
            )}
          </VStack>
        </AlertDialogBody>
        <AlertDialogFooter>
          <HStack className="justify-end w-full">
            <Button className="rounded-xl" onPress={onClose} variant="outline">
              <ButtonText>{i18n.t("misc.close")}</ButtonText>
            </Button>
          </HStack>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ShareResultsModal;
